/* ==========================================================================
   Quick Look persistence.

   Answers are kept in the browser so a busy owner can close the tab mid-way
   and pick up later, and so a return visit can show them what they already
   found. Only the answers are stored — never the question text — so the file
   stays small and the questions stay editable.

   Nothing in here inspects what it is holding. The record is a bag of answers
   keyed by question, and whether any given one still means anything is decided
   where the questions are, in quick-look-model.js: a key that names an option
   fills that question in, one that does not leaves it to be asked. So there is
   no version to check and nothing here to invalidate.
   ========================================================================== */

import { coreAnswered } from 'files/quick-look-model.js';

const KEY = 'dura.quick-look';

/* Fired whenever the record changes. The resume strip in the nav is mounted for
   the whole visit, so without this it would still be advertising "pick up where
   you left off" to someone who just finished, and would stay invisible to
   someone who just started. The browser's own `storage` event is no use here —
   it only fires in other tabs. */
export const CHANGED = 'dura.quick-look.changed';

const announce = () => {
    try {
        window.dispatchEvent(new CustomEvent(CHANGED));
    } catch(e) {}
}

/* Whether the resume strip has been dismissed for this tab.
 *
 * sessionStorage rather than local, deliberately. Closing the strip means "not
 * while I'm reading this", not "never again" — and it is the only way back to a
 * half-finished Quick Look, so a permanent dismissal would quietly discard the
 * answers it exists to protect.
 *
 * It lives here rather than in the component that renders the strip because
 * save() has to be able to clear it, and the rule that makes it clear is a rule
 * about the record, not about the chrome: opening the Quick Look and answering
 * something is a deliberate move toward the thing, and it should outrank a
 * dismissal made before it. Someone who closes the strip, reads two articles,
 * then goes back and adds three answers has plainly changed their mind, and the
 * way back should be waiting for them when they leave the tool.
 *
 * Progress, not opening. Opening and closing again without answering anything
 * leaves the dismissal where it was — that is not a change of mind, and putting
 * the strip back for it would make the dismissal feel broken.
 */
const DISMISSED = 'dura.quick-look.dismissed';

/* Held in memory as well as in storage, because private browsing throws on
   write. Without this the dismiss button does nothing at all there: the write
   fails, the read that follows says "not dismissed", and the strip never goes
   away no matter how many times it is closed. In memory it survives navigation
   within the app, which is a single page, and is lost on reload — which is the
   same shape of promise sessionStorage makes anyway. */
let dismissedHere = false;

export const isDismissed = () => {
    if(dismissedHere) return true;
    try {
        return window.sessionStorage.getItem(DISMISSED) === '1';
    } catch(e) {
        return false;
    }
}

export const dismiss = () => {
    dismissedHere = true;
    try {
        window.sessionStorage.setItem(DISMISSED, '1');
    } catch(e) {}
    announce();
}

const undismiss = () => {
    dismissedHere = false;
    try {
        window.sessionStorage.removeItem(DISMISSED);
    } catch(e) {}
}

/* The key this feature used while it was called the Operations Scorecard. A
   returning owner may have a finished or half-finished record under it, and
   dropping that would lose answers they gave us in good faith — so the first
   read under the new key adopts the old record and clears it. Delete this and
   `adopt` once enough time has passed that nobody is carrying one. */
const LEGACY_KEY = 'dura.scorecard';

const adopt = () => {
    try {
        const legacy = window.localStorage.getItem(LEGACY_KEY);
        if(!legacy) return null;

        window.localStorage.setItem(KEY, legacy);
        window.localStorage.removeItem(LEGACY_KEY);
        return legacy;

    } catch(e) {
        return null;
    }
}

export const load = () => {
    try {
        const raw = window.localStorage.getItem(KEY) || adopt();
        if(!raw) return null;

        const record = JSON.parse(raw);
        return record && typeof record === 'object' ? record : null;

    } catch(e) {
        return null;
    }
}

export const save = patch => {
    try {
        const next = {
            ...(load() || {}),
            ...patch,
            updatedAt: new Date().toISOString()
        };
        window.localStorage.setItem(KEY, JSON.stringify(next));

        /* Any write is progress, and progress outranks a dismissal made before
           it. Cleared before the announcement so the strip re-reads both the
           record and the flag in the same pass and comes back on its own. */
        undismiss();
        announce();
        return next;

    } catch(e) {
        // private browsing and full quotas both throw here. Losing the resume
        // point is survivable; breaking the Quick Look is not.
        return null;
    }
}

export const clear = () => {
    try {
        window.localStorage.removeItem(KEY);
        // nothing left to resume, so a stale dismissal would outlive its subject
        undismiss();
        announce();
    } catch(e) {}
}

/* Read against the questions as they stand, never against a flag. A record can
   say it was finished and be wrong about it — the questions it was finished
   against may not be the ones we ask now — where "every core question has a
   usable answer" cannot be wrong, because it is checked against the set in front
   of the owner today. */
export const isComplete = record => Boolean(record && coreAnswered((record && record.answers) || {}));

export const isInProgress = record => Boolean(
    record && !isComplete(record) && record.answers && Object.keys(record.answers).length > 0
);
